import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'wouter-preact';
import { api } from '@/lib/teaching/teacher-api';
import { TeachingPage, Card } from '@/components/teaching/ui';
import { Btn, ErrorLine } from '@/components/teaching/form';
import { KIND_LABEL, showPick, type Kind, type Pick } from '@/components/teaching/Picker';

// "Notes for Ali": every note made with the button bottom right, each spot
// shown with its picture. "Show" opens the page and outlines the element.

type Note = { id: string; kind: Kind; text: string; created_at: string; done_at: string | null; picks: Pick[] };

const KIND_TONE: Record<Kind, string> = { improve: '#e8effb', feature: '#d3f0d9', broken: '#fde2e1' };

export function WorkspaceNotes() {
  const [, navigate] = useLocation();
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => api<Note[]>('GET', '/api/t/workspace-notes').then(setNotes, (e) => setError((e as Error).message));
  useEffect(() => { void load(); }, []);

  const act = async (fn: () => Promise<unknown>) => { try { await fn(); await load(); } catch (e) { setError((e as Error).message); } };
  const show = (p: Pick) => { showPick(p.selector); navigate(p.page); };

  return (
    <TeachingPage title="Notes for Ali" breadcrumb="Use the round button bottom right on any page to point at something and say what should change.">
      <ErrorLine error={error} />
      {notes && notes.length === 0 && <Card><div class="text-[13px] text-[var(--color-text-muted)]">No notes yet.</div></Card>}
      {notes?.map((n) => (
        <Card key={n.id} class={n.done_at ? 'opacity-60' : ''}>
          <div data-note={n.id} class="flex flex-col gap-3">
            <div class="flex flex-wrap items-center gap-2">
              <span class="rounded-md px-2 py-0.5 text-[12px] font-medium" style={{ background: KIND_TONE[n.kind], color: '#1a1a1a' }}>{KIND_LABEL[n.kind]}</span>
              <span class="text-[12px] text-[var(--color-text-muted)]">{new Date(n.created_at).toLocaleString([], { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}</span>
              {n.done_at && <span class="text-[12px] text-[var(--color-text-muted)]">Done</span>}
              <div class="ml-auto flex gap-2">
                <Btn onClick={() => act(() => api('POST', `/api/t/workspace-notes/${n.id}/done`, { done: !n.done_at }))}>{n.done_at ? 'Reopen' : 'Mark done'}</Btn>
                <Btn kind="danger" onClick={() => { if (confirm('Delete this note?')) void act(() => api('DELETE', `/api/t/workspace-notes/${n.id}`)); }}>Delete</Btn>
              </div>
            </div>
            {n.text && <div class="text-[14px] whitespace-pre-wrap">{n.text}</div>}
            {n.picks.length > 0 && (
              <div class="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                {n.picks.map((p, i) => (
                  <div key={i} class="rounded-xl border border-[var(--color-border)] p-2 flex flex-col gap-1.5">
                    <div class="h-32 rounded-lg bg-[var(--color-elevated)] overflow-hidden flex items-center justify-center">
                      {p.shot ? <img src={p.shot} alt={p.text} class="max-w-full max-h-full object-contain" /> : <span class="text-[12px] text-[var(--color-text-muted)]">No picture</span>}
                    </div>
                    <div class="text-[12.5px] line-clamp-2">{p.text || '(no text)'}</div>
                    <div class="flex items-center gap-2">
                      <span class="text-[11.5px] text-[var(--color-text-muted)] truncate">{p.page}</span>
                      <button type="button" onClick={() => show(p)} class="ml-auto text-[12.5px] underline text-[var(--color-accent)] shrink-0">Show</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      ))}
    </TeachingPage>
  );
}
